import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import React, { useRef, useState } from 'react';

import { postVenue } from '@/redux/actions/venue.actions';

import type { VenueImportRow } from './venue-csv';
import { parseVenueCsv } from './venue-csv';

export default function VenueCsvImport({
  onImported,
}: {
  onImported: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<VenueImportRow[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(false);
  const locked = useRef(false);
  const ready = rows.filter((row) => row.status === 'ready');
  const saved = rows.filter((row) => row.status === 'saved').length;
  const attempted = rows.some((row) =>
    ['saved', 'failed'].includes(row.status)
  );

  const readFile = async (file?: File) => {
    if (!file || locked.current) return;
    locked.current = true;
    setBusy(true);
    setRows([]);
    setError('');
    try {
      if (file.size > 5 * 1024 * 1024)
        throw new Error('Choose a CSV smaller than 5 MB.');
      setRows(await parseVenueCsv(await file.text()));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to read this CSV. Check its formatting.'
      );
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };

  const importRows = async () => {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    let created = 0;
    for (const row of ready) {
      let status: VenueImportRow['status'] = 'saved';
      let message = '';
      try {
        // Save sequentially to avoid flooding the API and report each result.
        // eslint-disable-next-line no-await-in-loop
        await postVenue({
          ...row.venue,
          active,
          verified: true,
          custom: false,
          can_buy: false,
          can_buy_online: false,
          can_smoke: false,
          hours: [],
          tags: [],
        });
        created += 1;
      } catch {
        status = 'failed';
        message =
          'Save could not be confirmed. Check the venue list before importing this row again.';
      }
      setRows((current) =>
        current.map((item) =>
          item.row === row.row ? { ...item, status, error: message } : item
        )
      );
    }
    locked.current = false;
    setBusy(false);
    if (created) onImported();
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-secondary mr-2"
        onClick={() => setOpen(true)}
      >
        Import CSV
      </button>
      <Dialog
        open={open}
        fullWidth
        maxWidth="lg"
        onClose={() => {
          if (!busy) setOpen(false);
        }}
      >
        <DialogTitle>Import venues from CSV</DialogTitle>
        <DialogContent>
          <p>
            Upload a CSV with name and address columns. Optional columns: phone,
            website, facebook, instagram. Full addresses must use US street,
            city, state ZIP format. For other addresses, use separate street,
            city, state, zip and country columns.
          </p>
          <p>
            Review the preview, then import. Repeated venues within this file
            are flagged; existing venues are not checked or updated. Other
            columns are ignored.
          </p>
          <label htmlFor="venue-csv-file">Venue CSV file</label>
          <input
            id="venue-csv-file"
            className="form-control"
            type="file"
            accept=".csv,text/csv"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              readFile(file);
            }}
          />
          {error && (
            <p role="alert" className="text-danger mt-2">
              {error}
            </p>
          )}
          {rows.length > 0 && (
            <>
              <label className="mt-3">
                <input
                  type="checkbox"
                  checked={active}
                  disabled={busy || attempted}
                  onChange={(event) => setActive(event.target.checked)}
                />{' '}
                Make imported venues active
              </label>
              <p role="status" aria-live="polite">
                {ready.length} ready · {saved} imported ·{' '}
                {rows.filter((row) => row.status === 'invalid').length} invalid
                · {rows.filter((row) => row.status === 'failed').length} failed
                {busy ? ' — Processing…' : ''}
              </p>
              <div
                className="table-responsive"
                style={{ maxHeight: 400, overflow: 'auto' }}
              >
                <table className="table table-sm">
                  <thead>
                    <tr>
                      {[
                        'Row',
                        'Name',
                        'Address',
                        'Phone',
                        'Website',
                        'Facebook',
                        'Instagram',
                        'Status',
                      ].map((title) => (
                        <th key={title} scope="col">
                          {title}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.row}>
                        <td>{row.row}</td>
                        <td>{row.venue.name}</td>
                        <td>
                          {[
                            row.venue.addr_line_1,
                            row.venue.addr_line_2,
                            row.venue.addr_city,
                            row.venue.addr_state,
                            row.venue.addr_zip,
                            row.venue.addr_country,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </td>
                        <td>{row.venue.phone}</td>
                        <td>{row.venue.website}</td>
                        <td>{row.venue.facebook_id}</td>
                        <td>{row.venue.instagram_id}</td>
                        <td className={row.error ? 'text-danger' : ''}>
                          {row.error || row.status}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            className="btn btn-light"
            disabled={busy}
            onClick={() => setOpen(false)}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !ready.length}
            onClick={() => {
              importRows();
            }}
          >
            {busy ? 'Processing…' : `Import ${ready.length} venues`}
          </button>
        </DialogActions>
      </Dialog>
    </>
  );
}
